import * as React from 'react';

import { on, throttle, checkVisible, ILazyLoad } from './utils';

// tslint:disable-next-line:no-any
let listeners: LazyLoad<any>[];

function scheduleLowPriorityTask(cb: () => void) {
  // tslint:disable-next-line:no-any
  const r = (window as any).requestIdleCallback;

  if (r) {
    r(cb, { timeout: 500 });
  } else {
    setTimeout(cb, 500);
  }
}

export interface ILazyLoadProps<T> {
  deferred?: boolean;
  offset?: number;

  onLoad(): Promise<T>;
  renderEmpty(): JSX.Element;
  renderLoading?(): JSX.Element;
  renderLoaded(result: T): JSX.Element;
  renderError?(e: Error): JSX.Element;
}

const lazyLoadHandler = () => {
  // tslint:disable-next-line:no-any
  const newListeners: LazyLoad<any>[] = [];

  for (let i = 0; i < listeners.length; ++i) {
    const listener = listeners[i];
    if (!checkVisible(listener)) {
      newListeners.push(listener);
    }
  }

  listeners = newListeners;
};

const loadFirstListener = () => {
  const componentIndex = listeners.findIndex(listener => !!listener.props.deferred);
  if (componentIndex !== -1) {
    const [component] = listeners.splice(componentIndex, 1);

    component.visible = true;
    component.forceUpdate();
  }

  scheduleLowPriorityTask(loadFirstListener);
};

const initLazyLoad = () => {
  if (typeof window === 'undefined' || listeners) {
    return;
  }

  listeners = [];

  const finalLazyLoadHandler = throttle(lazyLoadHandler, 300);

  on(window, 'scroll', finalLazyLoadHandler);
  on(window, 'resize', finalLazyLoadHandler);

  on(window, 'load', () => scheduleLowPriorityTask(loadFirstListener));
};

/**
 * @deprecated Необходимо отказываться от старой рекламы adfox и пользоваться библиотекой @cian/adfox-component,
 * туда lazyLoad уже встроен
 */
// eslint-disable-next-line import/no-default-export
export default class LazyLoad<T>
  extends React.Component<ILazyLoadProps<T>, {}>
  implements ILazyLoad<ILazyLoadProps<T>, {}>
{
  public visible = false;

  private status: 'waiting' | 'loading' | 'loaded' | 'error' = 'waiting';
  private content: Promise<T> | undefined;
  private loadResult: T | undefined;
  private loadError: Error | undefined;

  public componentDidMount() {
    initLazyLoad();

    if (!checkVisible(this)) {
      listeners.push(this);
    }
  }

  public componentWillUnmount() {
    listeners = listeners.filter(el => el !== this);
  }

  public UNSAFE_componentWillUpdate() {
    if (this.visible && this.status === 'waiting') {
      this.status = 'loading';
      this.content = this.props.onLoad();
      this.content
        .then((result: T) => {
          this.status = 'loaded';
          this.loadResult = result;
          this.forceUpdate();
        })
        .catch((e: Error) => {
          this.status = 'error';
          this.loadError = e;
          this.forceUpdate();
        });
    }
  }

  public render() {
    switch (this.status) {
      case 'waiting':
        return this.props.renderEmpty();
      case 'loading':
        if (this.props.renderLoading) {
          return this.props.renderLoading();
        }

        return this.props.renderEmpty();
      case 'loaded':
        return this.props.renderLoaded(this.loadResult as T);
      case 'error':
        if (this.props.renderError) {
          return this.props.renderError(this.loadError as Error);
        }

        return this.props.renderEmpty();
      default:
        return this.props.renderEmpty();
    }
  }
}
